import { openDB } from 'idb';
import { createClient } from '@supabase/supabase-js';
import type { Project, SavedProjectSummary } from '../types';
import { migrateProject } from './migrations';

const DB_NAME = 'arborcad-local';
const STORE = 'projects';
const ASSET_STORE = 'assets';
const ACTIVE_KEY = 'arborcad-active-project';

const dbPromise = openDB(DB_NAME, 2, {
  upgrade(db) {
    if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE, { keyPath: 'id' });
    if (!db.objectStoreNames.contains(ASSET_STORE)) db.createObjectStore(ASSET_STORE, { keyPath: 'id' });
  },
});

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string | undefined;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined;
export const supabase = supabaseUrl && supabaseAnonKey ? createClient(supabaseUrl, supabaseAnonKey) : null;

export async function saveProject(project: Project) {
  const updated = { ...project, schemaVersion: 2 as const, updatedAt: new Date().toISOString() };
  await (await dbPromise).put(STORE, updated);
  localStorage.setItem(ACTIVE_KEY, updated.id);

  if (supabase) {
    const { data } = await supabase.auth.getSession();
    if (data.session?.user) {
      const { error } = await supabase.from('projects').upsert({
        id: updated.id,
        user_id: data.session.user.id,
        name: updated.name,
        project_data: updated,
        schema_version: updated.schemaVersion,
        updated_at: updated.updatedAt,
      });
      if (error) throw error;
    }
  }
  return updated;
}

export async function saveTextureAsset(file: File) {
  const id = `texture_${crypto.randomUUID()}`;
  await (await dbPromise).put(ASSET_STORE, { id, blob: file, name: file.name, type: file.type, createdAt: new Date().toISOString() });
  let storagePath: string | undefined;
  if (supabase) {
    const { data } = await supabase.auth.getSession();
    if (data.session?.user) {
      storagePath = `${data.session.user.id}/${id}-${file.name.replace(/[^a-z0-9._-]+/gi, '-')}`;
      const { error } = await supabase.storage.from('material-textures').upload(storagePath, file, { cacheControl: '31536000', upsert: false });
      if (error) storagePath = undefined;
    }
  }
  return { id, storagePath };
}

export async function loadTextureAsset(id: string, storagePath?: string) {
  const row = await (await dbPromise).get(ASSET_STORE, id) as { blob: Blob } | undefined;
  if (row?.blob) return row.blob;
  if (!supabase || !storagePath) return undefined;
  const { data, error } = await supabase.storage.from('material-textures').download(storagePath);
  if (error || !data) return undefined;
  await (await dbPromise).put(ASSET_STORE, { id, blob: data, name: storagePath.split('/').at(-1), type: data.type, createdAt: new Date().toISOString() });
  return data;
}

export async function loadProject(id: string): Promise<Project | undefined> {
  const local = (await (await dbPromise).get(STORE, id)) as Project | undefined;
  if (local) return migrateProject(local);
  if (supabase) {
    const { data } = await supabase.from('projects').select('project_data').eq('id', id).maybeSingle();
    if (data?.project_data) return migrateProject(data.project_data as Project);
  }
  return undefined;
}

export async function loadActiveProject() {
  const id = localStorage.getItem(ACTIVE_KEY);
  return id ? loadProject(id) : undefined;
}

export async function listProjects(): Promise<SavedProjectSummary[]> {
  const items = (await (await dbPromise).getAll(STORE)) as Project[];
  return items.map(({ id, name, updatedAt }) => ({ id, name, updatedAt })).sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
}

export async function deleteProject(id: string) {
  await (await dbPromise).delete(STORE, id);
  if (localStorage.getItem(ACTIVE_KEY) === id) localStorage.removeItem(ACTIVE_KEY);
  if (supabase) {
    const { data } = await supabase.auth.getSession();
    if (data.session?.user) await supabase.from('projects').delete().eq('id', id).eq('user_id', data.session.user.id);
  }
}

export async function duplicateProject(project: Project) {
  const now = new Date().toISOString();
  const copy: Project = { ...structuredClone(project), id: `project_${crypto.randomUUID()}`, name: `${project.name} — copy`, createdAt: now, updatedAt: now };
  await saveProject(copy);
  return copy;
}
